import {
  BackButton,
  BaseFunctionProps,
  CopyCard,
  PageTitle,
  SubTitle,
} from '@foxtrotplatform/developer-platform-common-ui-component-lib';
import { useOrganization } from '@foxtrotplatform/platform-micro-site-core';
import { FunctionComponent, useEffect, useRef, useState } from 'react';

import {
  DeveloperOrganizationContextProps,
  TenantOnboardingStatus,
} from '../../../common/providers/types';

const POLL_TENANT_STATUS_INTERVAL = 1000 * 60 * 2; //Poll for tenant status every 2 minutes

const ONBOARDING_STATUS_DISPLAY_TEXT = {
  [TenantOnboardingStatus.IN_PROGRESS]:
    'Your organization has been created. We are reviewing your request for the sandbox environment. Our admins will reach out to you with a decision shortly.',
  [TenantOnboardingStatus.DECLINED]:
    'Your tenant creation request is not approved. Our product admin will contact you with additional details at the email address that you provided.',
};
const ONBOARDING_STATUS_DISPLAY_TEXT_CLASSNAME = {
  [TenantOnboardingStatus.IN_PROGRESS]: 'text-in-transit-2',
  [TenantOnboardingStatus.DECLINED]: 'text-error-icon',
};

interface OrganizationTenantInfoProps extends BaseFunctionProps {
  hideBackButton?: boolean;
}

export const OrganizationTenantInfo: FunctionComponent<
  OrganizationTenantInfoProps
> = ({ className, hideBackButton = false }): JSX.Element | null => {
  const { currentTenant: tenant, refreshOrganizationTenant } =
    useOrganization() as DeveloperOrganizationContextProps;

  const [uiStatus, setUiStatus] = useState<TenantOnboardingStatus>(
    tenant ? tenant.onboardingStatus : TenantOnboardingStatus.IN_PROGRESS
  );
  const pollTimeoutRef = useRef<NodeJS.Timeout>();

  const { uniqueCode = '', dnsEntry = '' } = tenant || {};

  useEffect(() => {
    if (uiStatus === TenantOnboardingStatus.IN_PROGRESS) {
      if (pollTimeoutRef) {
        clearTimeout(pollTimeoutRef.current);
      }
      pollTimeoutRef.current = setTimeout(
        refreshOrganizationTenant,
        POLL_TENANT_STATUS_INTERVAL
      );
    } else {
      if (pollTimeoutRef) {
        clearTimeout(pollTimeoutRef.current);
      }
    }
  }, [refreshOrganizationTenant, uiStatus, pollTimeoutRef]);

  useEffect(() => {
    if (!tenant) {
      return;
    }

    let status = TenantOnboardingStatus.IN_PROGRESS;
    if (tenant.onboardingStatus === TenantOnboardingStatus.SUCCESS) {
      status = TenantOnboardingStatus.SUCCESS;
    }
    if (tenant.onboardingStatus === TenantOnboardingStatus.DECLINED) {
      status = TenantOnboardingStatus.DECLINED;
    }
    setUiStatus(status);
  }, [tenant]);

  return (
    <div className={className}>
      <div className="flex flex-row items-center gap-x-3 mb-os1-spacing">
        {hideBackButton || <BackButton />}
        <PageTitle>Tenant Info</PageTitle>
      </div>
      {uiStatus !== TenantOnboardingStatus.SUCCESS && (
        <SubTitle
          className={`${ONBOARDING_STATUS_DISPLAY_TEXT_CLASSNAME[uiStatus]} max-w-xl`}
        >
          {ONBOARDING_STATUS_DISPLAY_TEXT[uiStatus]}
        </SubTitle>
      )}
      {uiStatus === TenantOnboardingStatus.SUCCESS && (
        <div className="flex md:flex-row items-center flex-col gap-x-3">
          <CopyCard label="Tenant ID" value={uniqueCode} />
          <div className="h-11 border-r" />
          <CopyCard
            label="Tenant URL"
            value={dnsEntry}
            valueElement={
              <a href={`https://${dnsEntry}`} rel="noreferrer" target="_blank">
                {dnsEntry}
              </a>
            }
          />
        </div>
      )}
    </div>
  );
};
